import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, X, ExternalLink, Navigation } from 'lucide-react';

/**
 * AddressLink 컴포넌트
 * 주소를 클릭하면 지도 앱 선택 모달을 띄워서 네이버지도/카카오맵/티맵으로 이동
 */
export default function AddressLink({ address, className = '', masked = false }) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ESC 키로 모달 닫기 (이벤트 전파 중단하여 상세모달은 유지)
    React.useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isModalOpen) {
                e.stopPropagation();
                e.preventDefault();
                setIsModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleEsc, true); // capture phase
        return () => window.removeEventListener('keydown', handleEsc, true);
    }, [isModalOpen]);

    if (!address || address === '-') {
        return <span className={className}>-</span>;
    }

    if (masked) {
        return <span className={className}>{address}</span>;
    }

    // URL 인코딩된 주소
    const encodedAddress = encodeURIComponent(address);

    // 지도 앱 링크들
    const mapApps = [
        {
            name: '네이버지도',
            icon: '/images/navermap.png',
            color: 'bg-white',
            url: `https://map.naver.com/v5/search/${encodedAddress}`,
        },
        {
            name: '카카오맵',
            icon: '/images/kakaomap.png',
            color: 'bg-white',
            url: `https://map.kakao.com/?q=${encodedAddress}`,
        },
        {
            name: '티맵',
            icon: '/images/tmap.png',
            emoji: '🚗',  // 아이콘 없을 시 fallback
            color: 'bg-white',
            url: `tmap://search?name=${encodedAddress}`,  // 딥링크 (앱 직접 호출)
            isDeepLink: true,  // 딥링크 표시
        },
        {
            name: '구글맵',
            icon: '/images/googlemap.png',
            color: 'bg-white',
            url: `https://www.google.com/maps/search/${encodedAddress}`,
        },
        {
            name: '클립보드 복사',
            icon: null,
            emoji: '📋',
            color: 'bg-gray-100',
            action: 'copy',
        },
    ];

    const handleMapClick = async (app) => {
        if (app.action === 'copy') {
            try {
                await navigator.clipboard.writeText(address);
                setIsModalOpen(false);
                // 복사 완료 알림 (간단한 alert 대신 나중에 toast로 개선 가능)
            } catch (err) {
                console.error('클립보드 복사 실패:', err);
            }
        } else {
            window.open(app.url, '_blank');
            setIsModalOpen(false);
        }
    };

    return (
        <>
            {/* 클릭 가능한 주소 텍스트 */}
            <button
                onClick={() => setIsModalOpen(true)}
                className={`inline-flex items-center gap-1 ${className} text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors max-w-full overflow-hidden`}
                title="지도 앱으로 열기"
            >
                <MapPin size={14} className="flex-shrink-0" />
                <span className="truncate max-w-[180px]">{address}</span>
            </button>

            {/* 지도 앱 선택 모달 */}
            {isModalOpen && createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center"
                    onClick={() => setIsModalOpen(false)}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-[90%] max-w-sm mx-4 overflow-hidden border border-gray-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* 헤더 */}
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Navigation size={20} />
                                <h3 className="font-bold text-lg">지도에서 열기</h3>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-1.5 hover:bg-white/20 rounded-full transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* 주소 표시 */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                            <p className="text-sm text-gray-500 font-medium">선택한 주소</p>
                            <p className="text-gray-800 font-bold break-words">{address}</p>
                        </div>

                        {/* 지도 앱 목록 */}
                        <div className="p-4 grid grid-cols-2 gap-3">
                            {mapApps.map((app) => (
                                <button
                                    key={app.name}
                                    onClick={() => handleMapClick(app)}
                                    className={`
                    flex flex-col items-center gap-2 p-4 rounded-2xl border border-gray-100 
                    hover:border-blue-200 hover:bg-blue-50/50 transition-all
                    active:scale-95
                  `}
                                >
                                    <div className={`w-12 h-12 ${app.color} rounded-xl flex items-center justify-center shadow-sm overflow-hidden border border-gray-100`}>
                                        {app.icon ? (
                                            <img src={app.icon} alt={app.name} className="w-10 h-10 object-contain" />
                                        ) : (
                                            <span className="text-2xl">{app.emoji}</span>
                                        )}
                                    </div>
                                    <span className="text-sm font-bold text-gray-700">{app.name}</span>
                                </button>
                            ))}
                        </div>

                        {/* 취소 버튼 */}
                        <div className="px-4 pb-4">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="w-full py-3 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
